/**
 * @typedef {Object} EventOptions
 * @property {boolean} [bubbles=false]
 * @property {boolean} [composed=false]
 */

/**
 * Mixin which adds `fire` method
 *
 * @param {*} superclass the class to mix in to
 * @return {EventMixinElement}  The mixed class
 * @mixin EventMixin
 */
export const EventMixin = superclass =>
  /**
   * @template SuperClass
   * @extends {HTMLElement, SuperClass}
   */
  class EventMixinElement extends superclass {
    /** @inheritdoc */
    constructor() {
      super();
      // holds bound events so they can be unbound
      this._boundEvents = [];
      this._eventTypes = [];
      this.bindEvents.bind(this);
    }

    _cleanStyles(template) {
      const styleFirst = template.split('</style>').filter(x => !x.includes('<style>')).join('');
      const styleLast = template.split('<style>').filter(x => !x.includes('</style>')).join('');
      return styleFirst.length > 0 ? styleFirst : styleLast;
    }

    /**
     * bind events with a 'on-' prefix
     */
    bindEvents() {
      if (this.root && !this.renderer) {
        const template = this._cleanStyles(this.root.innerHTML);

        if (!template.includes('on-')) {
          return;
        }

        if (!(template.split('on-')[1]).includes('="')) {
          return;
        }

        this._eventTypes = (template
          .split('on-') || [])
          .map(event => event.split('=')[0])
          .filter(x => !x.includes('<') && !x.includes(' ') && !x.includes('"'));

        if (this._eventTypes.length === 0) {
          return;
        }

        this._eventTypes.forEach(eventType => {
          const attr = `on-${eventType}`;
          this.root.querySelectorAll(`[${attr}]`).forEach(el => {
            const event = {
              el,
              eventType,
              fn: this[el.getAttribute(attr)].bind(this),
            };
            this.off(el, eventType, event.fn);
            this.on(el, eventType, event.fn);
            this._boundEvents.push(event);
          });
        });
      }
    }

    disconnectedCallback() {
      if (super.disconnectedCallback) {
        super.disconnectedCallback();
      }
      this._boundEvents.forEach(boundEvent => {
        this.off(boundEvent.el, boundEvent.eventType, boundEvent.fn);
      });
    }

    /**
     * bind a listener (callback) to el
     * @param {HTMLElement} el - element to listen to
     * @param {String} type - event name
     * @param {Function} callback - event callback
     */
    on(el, type, callback) {
      el.addEventListener(type, callback);
    }

    /**
     * unbind a listener (callback) from el
     * @param {HTMLElement} el - element to listen to
     * @param {String} type - event name
     * @param {Function} callback - event callback
     */
    off(el, type, callback) {
      el.removeEventListener(type, callback);
    }

    /**
     * Events a CustomEvent with an optional supplied detail.
     *
     * Eventd events do not bubble and are not composed. For composed events,
     * use `dispatchEvent`.
     *
     * @param  {String} type   CustomEvent type
     * @param  {any=} detail detail value
     * @param  {EventOptions=} options options initializer
     * @return {Boolean}
     * @memberof EventMixin#
     */
    fire(type, detail, { bubbles = false, composed = false } = {}) {
      return this.dispatchEvent(
        new CustomEvent(type, { bubbles, composed, detail }),
      );
    }
  };
